/**
 * Ticket Quality — dashboard qualita apertura ticket per FO / hotel.
 *
 * Metriche (per creator o per hotel):
 *  - Score composito 0-100
 *  - Duplicati (finestra `duplicate_days`)
 *  - Categoria corretta da altri (miscategorization)
 *  - Priorita declassata dopo (crying wolf)
 *  - Ticket incompleti (no foto + descrizione breve)
 *  - Descrizione troppo breve
 *
 * RBAC: FO vede solo se stesso (auto-coaching); admin/manager vedono tutto lo scope loro.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Trophy, AlertTriangle, User, Building2,
  ThumbsUp, Copy, Tag, ArrowDownRight, FileText, Camera, ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const CLASS_META = {
  ottimo:         { label: 'Ottimo',        color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' },
  buono:          { label: 'Buono',         color: 'bg-blue-100 text-blue-800 border-blue-200',         dot: 'bg-blue-500' },
  da_migliorare:  { label: 'Da migliorare', color: 'bg-amber-100 text-amber-800 border-amber-200',       dot: 'bg-amber-500' },
  critico:        { label: 'Critico',       color: 'bg-red-100 text-red-800 border-red-200',             dot: 'bg-red-500' },
};

const PROBLEM_LABELS = {
  duplicato: 'Duplicato',
  categoria_corretta_da_altri: 'Categoria sbagliata',
  priorita_declassata: 'Priorita esagerata',
  descrizione_breve: 'Descrizione breve',
  incompleto_senza_foto: 'Senza foto',
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return iso; }
};

const MetricCell = ({ icon: Icon, label, pct, count }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
    <div className="text-xs">
      <span className={`font-semibold ${pct > 20 ? 'text-red-600' : pct > 10 ? 'text-amber-600' : 'text-slate-700'}`}>
        {pct}%
      </span>
      <span className="text-slate-400 ml-1">({count})</span>
    </div>
  </div>
);

const TicketQualityPage = () => {
  const { user } = useAuth();
  const isFO = user?.role === 'frontoffice';
  const [scope, setScope] = useState(isFO ? 'user' : 'user');
  const [periodDays, setPeriodDays] = useState(30);
  const [duplicateDays] = useState(7); // fisso 7 come da scelta utente
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailUser, setDetailUser] = useState(null);
  const [detailIssues, setDetailIssues] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const p = new URLSearchParams({ scope, period_days: String(periodDays), duplicate_days: String(duplicateDays) });
      const { data: res } = await api.get(`/analytics/ticket-quality?${p.toString()}`);
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento qualita ticket');
    } finally {
      setLoading(false);
    }
  }, [scope, periodDays, duplicateDays]);

  useEffect(() => { load(); }, [load]);

  // Se FO, apri automaticamente il dettaglio su se stesso
  useEffect(() => {
    if (!isFO || !data || detailUser) return;
    const me = (data.items || []).find(i => i.key === user?.id);
    if (me) openDetail(me);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFO, data]);

  const openDetail = async (item) => {
    if (scope !== 'user') return;
    setDetailUser(item);
    setDetailIssues([]);
    try {
      setDetailLoading(true);
      const p = new URLSearchParams({ period_days: String(periodDays), duplicate_days: String(duplicateDays) });
      const { data: res } = await api.get(`/analytics/ticket-quality/user/${item.key}/issues?${p.toString()}`);
      setDetailIssues(res.issues || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento dettaglio');
    } finally {
      setDetailLoading(false);
    }
  };

  const bestWorst = useMemo(() => {
    if (!data?.items) return { best: null, worst: null };
    const list = data.items.filter(i => i.total_tickets >= 3); // ignora chi ha aperto pochi ticket
    if (list.length === 0) return { best: null, worst: null };
    return { best: list[0], worst: list[list.length - 1] };
  }, [data]);

  const ScopeIcon = scope === 'user' ? User : Building2;

  return (
    <div className="space-y-6 max-w-7xl" data-testid="ticket-quality-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Qualita Ticket</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isFO
              ? 'Il tuo punteggio di qualita nell\'apertura dei ticket — usa il dettaglio per migliorare.'
              : 'Classifica di FO e hotel in base a completezza, duplicati e correttezza categoria/priorita.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isFO && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-40" data-testid="tq-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Per persona (FO)</SelectItem>
                <SelectItem value="hotel">Per hotel</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Select value={String(periodDays)} onValueChange={(v) => setPeriodDays(Number(v))}>
            <SelectTrigger className="w-36" data-testid="tq-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 giorni</SelectItem>
              <SelectItem value="30">30 giorni</SelectItem>
              <SelectItem value="90">90 giorni</SelectItem>
              <SelectItem value="180">180 giorni</SelectItem>
              <SelectItem value="365">1 anno</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="tq-reload">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Top / Bottom highlight */}
      {!loading && !isFO && bestWorst.best && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-emerald-200/70 bg-emerald-50/40">
            <CardContent className="p-4 flex items-center gap-4">
              <Trophy className="w-8 h-8 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Migliore</p>
                <p className="text-lg font-bold text-slate-900">{bestWorst.best.name}</p>
                <p className="text-xs text-slate-500">
                  Score {bestWorst.best.score} · {bestWorst.best.total_tickets} ticket
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200/70 bg-red-50/40">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-[11px] uppercase tracking-wide text-red-700 font-semibold">Da migliorare</p>
                <p className="text-lg font-bold text-slate-900">{bestWorst.worst.name}</p>
                <p className="text-xs text-slate-500">
                  Score {bestWorst.worst.score} · {bestWorst.worst.total_tickets} ticket
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Classifica */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ScopeIcon className="w-4 h-4 text-slate-500" />
            Classifica {scope === 'user' ? 'Front Office' : 'Hotel'}
            <Badge variant="secondary" className="ml-2 font-normal">{data?.total_tickets ?? 0} ticket analizzati</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (!data?.items || data.items.length === 0) ? (
            <div className="p-10 text-center text-slate-500 text-sm">Nessun ticket nel periodo selezionato.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">{scope === 'user' ? 'Persona' : 'Hotel'}</th>
                    <th className="px-4 py-2 text-left">Score</th>
                    <th className="px-4 py-2 text-left">Ticket</th>
                    <th className="px-4 py-2 text-left">Duplicati</th>
                    <th className="px-4 py-2 text-left">Cat. errata</th>
                    <th className="px-4 py-2 text-left">Prio. declassata</th>
                    <th className="px-4 py-2 text-left">Incompleti</th>
                    {scope === 'user' && <th className="px-4 py-2"></th>}
                  </tr>
                </thead>
                <tbody data-testid="tq-tbody">
                  {data.items.map((it, idx) => {
                    const cls = CLASS_META[it.classification] || CLASS_META.buono;
                    return (
                      <tr
                        key={it.key}
                        className={`border-t border-slate-100 hover:bg-slate-50/60 ${scope === 'user' ? 'cursor-pointer' : ''}`}
                        onClick={() => scope === 'user' && openDetail(it)}
                        data-testid={`tq-row-${idx}`}
                      >
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${cls.dot}`} />
                            <span className="font-medium text-slate-800">{it.name}</span>
                            {it.role && <Badge variant="outline" className="text-[10px] font-normal">{it.role}</Badge>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-slate-900">{it.score}</span>
                            <Badge className={`text-[10px] font-normal border ${cls.color}`}>{cls.label}</Badge>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{it.total_tickets}</td>
                        <td className="px-4 py-3"><MetricCell icon={Copy} pct={it.rates.duplicate_rate} count={it.duplicates} /></td>
                        <td className="px-4 py-3"><MetricCell icon={Tag} pct={it.rates.miscategorization_rate} count={it.miscategorized} /></td>
                        <td className="px-4 py-3"><MetricCell icon={ArrowDownRight} pct={it.rates.priority_downgrade_rate} count={it.priority_downgraded} /></td>
                        <td className="px-4 py-3"><MetricCell icon={FileText} pct={it.rates.low_completeness_rate} count={it.low_completeness} /></td>
                        {scope === 'user' && (
                          <td className="px-4 py-3 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dettaglio persona */}
      {scope === 'user' && detailUser && (
        <Card data-testid="tq-detail-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <User className="w-4 h-4 text-slate-500" />
              Dettaglio: {detailUser.name}
              <Badge className={`ml-2 border ${CLASS_META[detailUser.classification]?.color}`}>
                Score {detailUser.score} — {CLASS_META[detailUser.classification]?.label}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {detailLoading ? (
              <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : detailIssues.length === 0 ? (
              <div className="p-6 text-center text-emerald-700 flex items-center justify-center gap-2 text-sm">
                <ThumbsUp className="w-5 h-5" />
                Nessun ticket problematico nel periodo. Ottimo lavoro!
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">
                  {detailIssues.length} ticket con problemi — ecco cosa puoi migliorare:
                </p>
                {detailIssues.map((iss) => (
                  <div key={iss.id} className="flex items-start justify-between gap-3 p-3 border border-slate-100 rounded-lg hover:bg-slate-50/60">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link to={`/tickets/${iss.id}`} className="text-sm font-medium text-slate-800 hover:text-blue-600 truncate">
                          {iss.title || '(senza titolo)'}
                        </Link>
                        <span className="text-xs text-slate-400">· {fmtDate(iss.created_at)}</span>
                        {iss.area && <Badge variant="outline" className="text-[10px] font-normal">{iss.area}</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {iss.problems.map(p => (
                          <Badge key={p} className="bg-amber-100 text-amber-800 text-[10px] font-normal">
                            {PROBLEM_LABELS[p] || p}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info metodologia */}
      <Card className="bg-slate-50/60 border-slate-200/60 dark:bg-slate-800/50 dark:border-slate-700">
        <CardContent className="p-4 text-xs text-slate-700 dark:text-slate-200 leading-relaxed">
          <p className="font-semibold text-slate-900 dark:text-white mb-1">Come calcoliamo lo score</p>
          <p>
            <strong>Duplicato</strong>: stesso hotel + area + categoria entro 7 giorni (peso -40).
            <strong className="ml-2">Categoria errata</strong>: manager/capo ha corretto la categoria (peso -20).
            <strong className="ml-2">Priorita esagerata</strong>: admin ha declassato priorita (peso -15).
            <strong className="ml-2">Incompleti</strong>: senza foto e descrizione &lt; 20 caratteri (peso -15).
            <strong className="ml-2">Descrizione breve</strong>: &lt; 20 caratteri (peso -10).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketQualityPage;
